import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { v2, v2Unscoped, v2api, idempotencyKey, V2ApiError, type V2Envelope } from './v2'
import { useSessionStore } from '@/stores/sessionStore'
import type { DiscountOverrideRow } from '@/lib/cart'


export const receiptKeys = {
  all: ['receipts'] as const,
  current: () => [...receiptKeys.all, 'current'] as const,
  detail: (id: number) => [...receiptKeys.all, 'detail', id] as const,
  history: (customerId?: number) => [...receiptKeys.all, 'history', customerId] as const,
}

// Mutation key shared by useUpdateReceiptItem and useDeleteReceiptItem
// so consumers can `useIsMutating({ mutationKey: receiptItemMutationKey })`
// to detect ANY in-flight item mutation across all hook instances
// (CartItemRow's X / qty buttons AND ProductModal's lot/qty editor in
// refund mode). Without this, isPending on a single useDeleteReceiptItem
// instance wouldn't see a mutation kicked off by another instance --
// the Process Refund button could then fire while ProductModal's PUT
// was still in flight and totals were stale.
export const receiptItemMutationKey = ['receipts', 'item-mutation'] as const


// String labels the BE search-receipts endpoint returns for kiosk_status.
// Mirrors the status maps in search.py (lines 121-125, 148-151, 188-191).
// The 'Voided' / 'Canceled' labels appear in specific search-query branches
// only -- the main ecom-orders list returns the first four. Keep the union
// open to all so consumers don't false-positive-narrow on the common case.
export type KioskStatusLabel =
  | 'New'
  | 'Preparing'
  | 'Ready'
  | 'Completed'
  | 'Voided'
  | 'Canceled'

export interface ReceiptItem {
  id: number
  product_id: number
  qty: number
  price: number        // net stored price (pre-deposit, may be tax-inclusive for cannabis)
  total?: number | string  // gross line total including deposit + tax (what customer pays)
  discount: number
  discount_amount: number
  subtotal: number
  deposit_amount?: number | string  // deposit/recycling fee charged on this line
  tax1?: number | string   // HST amount for this line
  taxes?: number | string  // total taxes for this line
  is_cannabis?: boolean
  unit?: string
  description?: string
  lot_number?: string
  cspcid?: string
  // Basket linkage. Always present in the v2 response, null on non-basket
  // lines. When `basket_id` is set this row is a child of that basket SKU
  // (the basket itself is never persisted as a receipt line). Edits to
  // basket children are rejected by the backend; deletes cascade to all
  // siblings. See V2-API.md § Baskets.
  basket_id?: number | null
  basket_qty?: string | number | null
  // Quantity already refunded against this line across all non-canceled
  // returns of the receipt (source receipts only; 0 on return receipts).
  // Absent on pre-split-refund backends — treat missing as 0.
  returned_qty?: number | string
  remaining_qty?: number | string
}

export interface ReceiptTotals {
  subtotal: number | string
  tax?: number | string
  taxes?: number | string   // actual field name in API response
  tax1?: number | string
  tax2?: number | string
  tax3?: number | string
  tax4?: number | string
  discount?: number | string
  discount_amount?: number | string
  deposit_amount?: number | string
  total: number | string
}

export interface Receipt {
  id: number
  number: string
  /** Originating store (v2 build_full_payload). Absent on older portals;
   *  OrderDetailModal treats unknown as same-store. */
  store_id?: number | null
  store_name?: string | null
  timestamp: string
  status: number
  customer_id: number | null
  customer?: {
    id?: number
    display_name: string
    first_name?: string
    last_name?: string
    is_blocked?: boolean
  } | null
  items: ReceiptItem[]
  totals: ReceiptTotals
  amount_to_pay: number
  on_hold: boolean
  // Refund linkage — fields differ by which side of the relationship the
  // receipt is on:
  //   REFUND side (the receipt created via /copy-with-refund):
  //     `is_return: true`, `src_id`, plus `src_number` / `source_number`
  //     pointing back to the original sale. v2 prefers `source_number`;
  //     legacy uses `src_number`. We accept both.
  //   SOURCE side (the original sale that was refunded):
  //     `return_number` / `return_total` / `return_receipt_id` point
  //     forward to the refund. Legacy v1 shape; v2 uses `is_return` on
  //     the refund side instead.
  is_return?: boolean
  src_id?: number
  src_number?: string
  source_number?: string
  return_number?: string
  return_total?: number | string
  return_receipt_id?: number
  // Origin-channel flags from the BE serializer. `online && !instorekiosk`
  // identifies an ecom order (waiting for a clerk to prep + finish). The
  // orthogonal `kiosk_status` advances 0 -> 1 -> 2 -> 3 as the order moves
  // through the prep pipeline. Drives the Prepare/Finish buttons in
  // OrderDetailModal.
  online?: boolean
  instorekiosk?: boolean
  // True while a captured online (Moneris Checkout) card payment still
  // holds money (not fully refunded). Untouched orders get the one-click
  // full refund (useRefundOnlinePayment); partial refunds go through the
  // normal copy-with-refund cart with the Online Card tender.
  has_captured_online_payment?: boolean
  // Redacted display block for the online card payment (ecom captures
  // book no Payment JT, so `payments` is empty for them). Present once
  // money was captured; stays after a refund so the modal can show it.
  online_payment?: {
    provider?: string | null
    amount?: string | number | null
    card_type?: string | null
    card_last_four?: string | null
    refunded?: boolean
    refund_kind?: string | null
    // Cumulative across partial refunds.
    refunded_amount?: string | number | null
    // What the gateway can still credit; == amount on an untouched order.
    refund_remaining?: string | number | null
  } | null
  // Delivery flags from the BE serializer. `is_delivery` marks an ecom
  // delivery order; `delivery_status` is its current fulfillment state
  // (see DELIVERY_STATUS). Drive the OrderDetailModal delivery layout and
  // the "ready for delivery" message wording.
  is_delivery?: boolean
  delivery_status?: string | null
  // The BE serializes this differently per endpoint:
  //   - /receipts/{id} (build_full_payload) returns the raw integer
  //     (0=New, 1=Preparing, 2=Ready, 3=Completed) -- OrderDetailModal
  //     consumes this shape.
  //   - /receipts/search?kiosk=true (the ecom-orders list) maps the
  //     integer to a string label ('New' | 'Preparing' | 'Ready' |
  //     'Completed', plus 'Voided' / 'Canceled' in edge branches) --
  //     OrdersTab / TabsBar consume this shape.
  // The union here reflects both. Consumers gate on `typeof` before
  // comparing -- OrderDetailModal checks `=== 'number'`, OrdersTab and
  // TabsBar check against string literals. Don't collapse this back to
  // `number | null` even if it feels cleaner; the search endpoint
  // wouldn't match and pending-order filters would silently break.
  kiosk_status?: number | KioskStatusLabel | null
  // Optional fields some receipt payloads include (settled receipts, etc.)
  payments?: TransactionPayment[]
  amount_paid?: number | string
  change_due?: number | string
  register_name?: string
  user_name?: string
  clerk_name?: string
  /** Manager approvals of over-limit discounts (always present on a current
   *  backend). Shown as "approved by" under OrderDetailModal's Discount row. */
  discount_overrides?: DiscountOverrideRow[]
}

interface CreateReceiptRequest {
  customer_id?: number | null
}

interface AddItemRequest {
  product_id: number
  qty?: number
  price?: number
  lot_number?: string
  unit?: string
}

interface UpdateItemRequest {
  qty?: number
  price?: number
  discount?: number
  discount_amount?: number
  // Refund-mode batch swap: PUT the new lot onto an existing receipt
  // item so the BE re-attributes the inventory move and recomputes
  // taxes against the new batch. `null` clears the lot, omitted leaves
  // it unchanged.
  lot_number?: string | null
}


export function useCreateReceipt() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: CreateReceiptRequest): Promise<Receipt> => {
      const payload: Record<string, unknown> = {}
      if (data.customer_id) {
        payload.customer_id = data.customer_id
      }
      return v2api.post<Receipt>(v2('/receipts'), payload)
    },
    onSuccess: (receipt) => {
      queryClient.setQueryData(receiptKeys.detail(receipt.id), receipt)
    },
  })
}


// Plain (non-hook) receipt fetch, shared by useReceipt and imperative
// flows like the paid-online pickup load (useLoadPaidOnlineOrder).
export async function fetchReceiptDetail(
  receiptId: number,
  options?: { includeItems?: boolean },
): Promise<Receipt> {
  // Unscoped on purpose: the customer-history modal must be able to
  // open a receipt rung at a different store than the clerk's current
  // one. The scoped /stores/{sid}/registers/{rid}/receipts/{id} route
  // 404s on cross-store reads.
  return v2api.get<Receipt>(
    v2Unscoped(`/receipts/${receiptId}`),
    options?.includeItems === true ? { include_items: true } : undefined,
  )
}

export function useReceipt(receiptId: number | null, options?: { includeItems?: boolean }) {
  const includeItems = options?.includeItems === true
  return useQuery({
    queryKey: includeItems
      ? [...receiptKeys.detail(receiptId!), 'with-items']
      : receiptKeys.detail(receiptId!),
    queryFn: async (): Promise<Receipt> => {
      return fetchReceiptDetail(receiptId!, { includeItems })
    },
    enabled: !!receiptId,
    staleTime: 0,
  })
}

// GET v2('/receipts/lookup-by-number?number=<value>')
// Resolves a V2 receipt number (or the raw QR payload `RCP-<number>` — the
// server strips that prefix) to a numeric receipt id. Store-scoped: a receipt
// rung at another store 404s here, which is intentional since V2 numbers
// embed the originating store. Returns null on 404, throws on other errors.
export interface ReceiptLookupResult {
  receipt_id: number
  number: string
  status: number
}

export async function lookupReceiptByNumber(
  number: string,
): Promise<ReceiptLookupResult | null> {
  try {
    return await v2api.get<ReceiptLookupResult>(
      v2('/receipts/lookup-by-number'),
      { number },
    )
  } catch (err) {
    if (err instanceof V2ApiError && err.status === 404) return null
    throw err
  }
}

// ---------------------------------------------------------------------------
// Transactions — read-only "rich" view of a finalized receipt that includes
// item-level brand/SKU/description/image fields the lighter receipts payload
// omits. Powers the "Open previous order" modal launched from the customer
// activity log.
//
// GET v2('/transactions/:id')
// ---------------------------------------------------------------------------

// Transactions are intentionally loose: the legacy backend uses different
// field names than the receipts endpoint (e.g. `product` vs `product_id`,
// `quantity` vs `qty`) and includes a long tail of optional metadata. We
// declare the keys we know about and leave the rest as an index signature
// so the UI can read alternates without TS getting in the way.
export interface TransactionItem {
  id?: number
  product_id?: number
  product?: number | { id?: number; name?: string; description?: string }
  qty?: number | string
  quantity?: number | string
  price?: number | string
  total?: number | string
  discount?: number | string
  discount_amount?: number | string
  subtotal?: number | string
  taxes?: number | string
  tax1?: number | string
  is_cannabis?: boolean
  unit?: string
  unit_name?: string
  description?: string
  product_name?: string
  display_name?: string
  brand?: string | null
  brand_name?: string
  cspcid?: string
  sku?: string
  category?: string
  category_name?: string
  size?: string
  image?: string | null
  image_url?: string | null
  thumbnail_url?: string | null
  lot_number?: string
  [key: string]: unknown
}

export interface TransactionPayment {
  id?: number
  payment_type?: number
  payment_type_name?: string
  payment_method?: string
  amount?: number | string
  /** Integrated-terminal tip portion of `amount`. Both payloads ship
   *  `amount` tip-INCLUSIVE (the charged amount) as of 2026-07-09 --
   *  amount - tip_amount is the sale share. */
  tip_amount?: number | string | null
  last4?: string
  /** 'googlepay' / 'applepay' on an online wallet payment, else ''. */
  wallet?: string
  card_type?: string
  status?: string
  /** Processor's reference id (e.g. Moneris transactionId). Non-null
   *  iff the JT was driven by an integrated terminal -- absent for
   *  cash, standalone-card, gift-card, etc. The cashier UI keys cancel
   *  flow off this: present -> automatic void via DELETE; absent ->
   *  manual confirm-first refund flow. */
  transaction_id?: string | null
  [key: string]: unknown
}

export interface Transaction extends Omit<Receipt, 'items'> {
  items: TransactionItem[]
  payments?: TransactionPayment[]
  // Legacy "transactions" payload includes register / clerk metadata that the
  // lighter receipts endpoint doesn't model.
  register?: number | string
  register_name?: string
  store?: number | string
  store_name?: string | null
  user?: number | string
  user_name?: string
  clerk_name?: string
  amount_paid?: number | string
  change_due?: number | string
  // The transaction record points at its underlying receipt via one of these
  // fields. The receipt is the canonical source for items/totals/payments.
  receipt?: number | string | null
  receipt_id?: number | string | null
  receipt_obj?: number | string | null
}

export function useTransaction(transactionId: number | null) {
  return useQuery({
    queryKey: [...receiptKeys.all, 'transaction', transactionId] as const,
    queryFn: async (): Promise<Transaction> => {
      // Unscoped: paired with useReceipt; both must work for the
      // customer-history modal across stores.
      return v2api.get<Transaction>(v2Unscoped(`/transactions/${transactionId}`))
    },
    enabled: !!transactionId,
    staleTime: 1000 * 60 * 5,
  })
}


// PUT v2('/receipts/{id}') — header fields only (e.g. customer_id).
export function useUpdateReceipt() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ receiptId, data }: { receiptId: number; data: Partial<CreateReceiptRequest> }): Promise<Receipt> => {
      return v2api.put<Receipt>(v2(`/receipts/${receiptId}`), data)
    },
    onSuccess: (receipt) => {
      queryClient.setQueryData(receiptKeys.detail(receipt.id), receipt)
    },
  })
}

// PUT v2('/receipts/{id}/customer') — switch the receipt's customer
// mid-cart and trigger a server-side reprice of every non-cashier-touched
// line. Returns the full re-priced receipt. See V2-API.md §
// "Customer change & reprice" for the predicate.
//
// Use this instead of `useUpdateReceipt({ customer_id })` whenever the
// cashier picks a different customer on a receipt that already has a
// receiptId — `useUpdateReceipt` only sets the FK; this endpoint also
// recomputes tier prices, scheduled discounts, exempt-taxes, and the
// matching journal rows.
export function useChangeReceiptCustomer() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      receiptId,
      customerId,
    }: {
      receiptId: number
      customerId: number | null
    }): Promise<Receipt> => {
      return v2api.put<Receipt>(v2(`/receipts/${receiptId}/customer`), {
        customer_id: customerId,
      })
    },
    onSuccess: (receipt) => {
      queryClient.setQueryData(receiptKeys.detail(receipt.id), receipt)
    },
  })
}


export function useAddReceiptItem() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ receiptId, item }: { receiptId: number; item: AddItemRequest }): Promise<Receipt> => {
      return v2api.post<Receipt>(v2(`/receipts/${receiptId}/items`), item)
    },
    onSuccess: (receipt) => {
      queryClient.setQueryData(receiptKeys.detail(receipt.id), receipt)
    },
  })
}


export function useUpdateReceiptItem() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: receiptItemMutationKey,
    mutationFn: async ({
      receiptId,
      itemId,
      data
    }: {
      receiptId: number
      itemId: number
      data: UpdateItemRequest
    }): Promise<Receipt> => {
      return v2api.put<Receipt>(v2(`/receipts/${receiptId}/items/${itemId}`), data)
    },
    onSuccess: (receipt) => {
      queryClient.setQueryData(receiptKeys.detail(receipt.id), receipt)
    },
  })
}


export function useDeleteReceiptItem() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: receiptItemMutationKey,
    mutationFn: async ({ receiptId, itemId }: { receiptId: number; itemId: number }): Promise<Receipt> => {
      return v2api.delete<Receipt>(v2(`/receipts/${receiptId}/items/${itemId}`))
    },
    onSuccess: (receipt) => {
      queryClient.setQueryData(receiptKeys.detail(receipt.id), receipt)
    },
  })
}


// PUT v2('/receipts/{id}/void') — body {void: bool, reason?}.
// v2 rejects voids on paid receipts (HTTP 409, code: cannot_cancel_paid).
// Callers should disable the void button when the receipt is paid and route
// users to the refund flow (useCreateRefund) instead.
export function useVoidReceipt() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      receiptId,
      reason,
    }: {
      receiptId: number
      reason?: string
    }): Promise<Receipt> => {
      const payload: Record<string, unknown> = { void: true }
      if (reason) payload.reason = reason
      return v2api.put<Receipt>(v2(`/receipts/${receiptId}/void`), payload)
    },
    onSuccess: (receipt) => {
      queryClient.setQueryData(receiptKeys.detail(receipt.id), receipt)
      // Voiding a held receipt removes it from the held list; voiding
      // a non-held receipt may also affect the orders tab. Invalidate
      // both so the lists refresh on next render -- mirrors the pattern
      // in useHoldReceipt where either direction shifts list membership.
      queryClient.invalidateQueries({ queryKey: [...receiptKeys.all, 'held'] })
      queryClient.invalidateQueries({ queryKey: [...receiptKeys.all, 'orders'] })
    },
  })
}


// PUT v2('/receipts/{id}') — advance an ecom order's kiosk_status.
// Called by the "Prepare & send to printer" button in OrderDetailModal,
// which runs print first and then this hook to flip 0 (New) -> 1
// (Preparing). Distinct from useHoldReceipt — this doesn't touch the
// `on_hold` boolean or PosReceiptStatus, only the kiosk-pipeline flag.
export function useSetKioskStatus() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      receiptId,
      kioskStatus,
    }: {
      receiptId: number
      kioskStatus: number
    }): Promise<Receipt> => {
      return v2api.put<Receipt>(v2(`/receipts/${receiptId}`), {
        kiosk_status: kioskStatus,
      })
    },
    onSuccess: (receipt) => {
      queryClient.setQueryData(receiptKeys.detail(receipt.id), receipt)
      // OrderDetailModal subscribes to `[...detail(id), 'with-items']`
      // (useReceipt with includeItems: true). Setting just the bare
      // detail key would miss it and the open modal would keep
      // displaying the pre-mutation kiosk_status. Invalidate the entire
      // detail key family so any subscriber — with or without items —
      // refetches and the badge / button gating recomputes immediately.
      queryClient.invalidateQueries({ queryKey: receiptKeys.detail(receipt.id) })
      // Ecom tab filters by kiosk_status; bump the cache so the row's
      // pipeline badge reflects the new state on next render.
      queryClient.invalidateQueries({ queryKey: [...receiptKeys.all, 'orders'] })
    },
  })
}


// Delivery fulfillment states. Mirrors DeliveryStatus on the BE
// (project/frontend/models/pos.py). `none`/`preparing` precede the delivery
// leg; the rest are the actions the Deliveries tab exposes. There is no
// `retry` value — "Out for Delivery Again" just writes another
// `out_for_delivery` entry (the BE log makes the retry observable).
export const DELIVERY_STATUS = {
  NONE: 'none',
  PREPARING: 'preparing',
  OUT_FOR_DELIVERY: 'out_for_delivery',
  DELIVERED: 'delivered',
  DELIVERY_FAILED: 'delivery_failed',
  PICKUP_AT_STORE: 'pickup_at_store',
} as const

export type DeliveryStatus = (typeof DELIVERY_STATUS)[keyof typeof DELIVERY_STATUS]

// Human labels for the badge / buttons.
export const DELIVERY_STATUS_LABEL: Record<string, string> = {
  none: 'Not started',
  preparing: 'Preparing',
  out_for_delivery: 'Out for delivery',
  delivered: 'Delivered',
  delivery_failed: 'Delivery failed',
  pickup_at_store: 'Pickup at store',
}

// PUT v2('/receipts/{id}/delivery-status') — body { status, note? }.
// Records a delivery transition + appends a history row on the BE. Used by
// the Deliveries-tab action buttons. Invalidates every Orders tab so the
// row's badge updates and terminal moves drop it off the Deliveries list.
export function useSetDeliveryStatus() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      receiptId,
      status,
      note,
    }: {
      receiptId: number
      status: DeliveryStatus
      note?: string
    }): Promise<{ status: string; delivery_status: DeliveryStatus }> => {
      return v2api.put(v2(`/receipts/${receiptId}/delivery-status`), {
        status,
        note,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...receiptKeys.all, 'orders'] })
      // Refresh any open delivery timeline so the new transition shows.
      queryClient.invalidateQueries({
        queryKey: [...receiptKeys.all, 'delivery-history'],
      })
    },
  })
}


// One delivery-status transition (a row in pos_receipt_delivery_status_log).
export interface DeliveryStatusEntry {
  status: string
  note: string | null
  changed_at: string | null
  clerk_name: string | null
}

// GET v2('/receipts/{id}/delivery-status') — the delivery timeline,
// oldest first. Backs the delivery detail modal's history view.
export function useDeliveryStatusHistory(
  receiptId: number | null,
  enabled = true,
) {
  return useQuery({
    queryKey: [...receiptKeys.all, 'delivery-history', receiptId],
    queryFn: async (): Promise<DeliveryStatusEntry[]> => {
      const data = await v2api.get<{ history: DeliveryStatusEntry[] }>(
        v2(`/receipts/${receiptId}/delivery-status`),
      )
      return data?.history ?? []
    },
    enabled: enabled && receiptId != null,
    staleTime: 1000 * 10,
  })
}


// POST v2('/receipts/{id}/ready-message') — send the customer the
// "your order is ready" SMS and/or email. Wraps the v1
// `send_ready_message` endpoint; the BE handler lives at
// hypnotoad/handlers/api/v2/side_effects.py:V2ReadyMessageHandler.
//
// Used by the "Ready & Send Customer Message" button (kiosk_status 1 ->
// 2) and the "Send Reminder Message" button (kiosk_status stays at 2).
// The status transition is the caller's responsibility — this hook
// only sends the message.
export interface SendReadyMessageBody {
  send_sms: boolean
  send_email: boolean
  phone: string
  email: string
  /** Email body (and the SMS body on a backend without `sms_message`). */
  message: string
  /** One-segment SMS body (lib/readySms). */
  sms_message?: string
}

/** `error` is a code (sms_failed / sms_rejected); `message`, when present,
 *  is the SMS provider's own reason, shown to the cashier as-is. */
export interface SendReadyMessageResult {
  success: boolean
  error?: string
  message?: string
}

export function useSendReadyMessage() {
  return useMutation({
    mutationFn: async ({
      receiptId,
      body,
    }: {
      receiptId: number
      body: SendReadyMessageBody
    }): Promise<SendReadyMessageResult> => {
      return v2api.post<SendReadyMessageResult>(
        v2(`/receipts/${receiptId}/ready-message`),
        body,
      )
    },
  })
}


// PUT v2('/receipts/{id}/status') — body {status: int}.
// Backend's PosReceipt.set_status() guards Paid/Paying/Deleted (use the
// dedicated endpoints instead) and LOCKED source states. Used by the
// "Finish the Sale" button to flip an ecom order from Hold (4) -> New (0)
// so the cashier can ring it up at the register.
export function useSetReceiptStatus() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      receiptId,
      status,
    }: {
      receiptId: number
      status: number
    }): Promise<Receipt> => {
      return v2api.put<Receipt>(v2(`/receipts/${receiptId}/status`), { status })
    },
    onSuccess: (receipt) => {
      queryClient.setQueryData(receiptKeys.detail(receipt.id), receipt)
      // Status moves shift the receipt across Ecom / Held / Recent
      // boundaries; refresh every Orders tab on next render.
      queryClient.invalidateQueries({ queryKey: [...receiptKeys.all, 'orders'] })
    },
  })
}


// POST v2('/receipts/{id}/refund-online-payment') — FULL-order refund of a
// captured online (Moneris Checkout) card payment, back to the card that
// paid (card-not-present). Same-day the gateway voids (nothing settles);
// later it refunds. The backend then CANCELS the order (PaidOnline or
// picked-up Paid alike). Untouched orders only — once any partial refund
// exists the BE rejects this (invalid_amount); partial refunds go through
// copy-with-refund + the Online Card tender instead. The idempotency key
// stays stable per receipt (an order can only be fully refunded once), so
// a double-click / retry replays the first result instead of hitting the
// gateway twice.
export function useRefundOnlinePayment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      receiptId,
      reason,
    }: {
      receiptId: number
      reason?: string
    }): Promise<{
      success: boolean
      data?: {
        refund?: { kind: string; amount: string }
        order_cancelled?: boolean
      }
    }> => {
      return v2api.post(v2(`/receipts/${receiptId}/refund-online-payment`), {
        idempotency_key: `refund-online-${receiptId}`,
        reason: reason ?? 'POS refund',
      })
    },
    onSuccess: (_data, { receiptId }) => {
      queryClient.invalidateQueries({ queryKey: receiptKeys.detail(receiptId) })
      queryClient.invalidateQueries({ queryKey: [...receiptKeys.all, 'orders'] })
    },
  })
}


// POST v2('/receipts/{id}/complete-pickup') — pickup completion for an
// online-PAID (PaidOnline=32) ecom order. The money is already captured,
// so the order must NOT be loaded into the cart or re-charged; the
// backend flips PaidOnline -> Paid and marks it processed so it leaves
// the ecom queue.
export function useCompletePickup() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      receiptId,
    }: {
      receiptId: number
    }): Promise<unknown> => {
      return v2api.post<unknown>(v2(`/receipts/${receiptId}/complete-pickup`), {})
    },
    onSuccess: (_data, { receiptId }) => {
      queryClient.invalidateQueries({ queryKey: receiptKeys.detail(receiptId) })
      queryClient.invalidateQueries({ queryKey: [...receiptKeys.all, 'orders'] })
    },
  })
}


// PUT v2('/receipts/{id}/hold') — body {hold: bool}.
export function useHoldReceipt() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      receiptId,
      onHold,
    }: {
      receiptId: number
      onHold: boolean
    }): Promise<Receipt> => {
      return v2api.put<Receipt>(v2(`/receipts/${receiptId}/hold`), { hold: onHold })
    },
    onSuccess: (receipt) => {
      queryClient.setQueryData(receiptKeys.detail(receipt.id), receipt)
      // Either direction (hold or unhold) shifts the receipt across
      // the held / recent boundaries; invalidate so the Orders tab
      // and Held Receipts modal refresh on next render.
      queryClient.invalidateQueries({ queryKey: [...receiptKeys.all, 'held'] })
      queryClient.invalidateQueries({ queryKey: [...receiptKeys.all, 'orders'] })
    },
  })
}


// Payment type integers — mirrors project/journal/models/payments.py
export const PAYMENT_TYPES = {
  CASH: 211,
  EXCHANGE: 212,
  MASTER_CARD: 213,
  POINTS: 214,
  DEBIT: 215,
  VISA: 216,
  AMEX: 217,
  CHANGE: 218,
  E_TRANSFER: 232,
  AR: 250,
  GIFT_CARD: 251,
  PAY_FROM_ACC: 260,
  CHEQUE: 275,
  ROUNDING_DIFF: 280,
  OTHER_CC: 281,
  SPRINGBIG_POINTS: 299,
} as const

export type PaymentTypeNumber = typeof PAYMENT_TYPES[keyof typeof PAYMENT_TYPES]

interface PaymentRequest {
  payment_type: PaymentTypeNumber
  /** Optional when ``items`` is also sent: BE recomputes total from items
   *  and charges that. Send an explicit ``amount`` only for split-tender
   *  (pay less than the recomputed balance on this method). */
  amount?: number
  /** Current local cart contents. When present, the BE truncate-and-replaces
   *  the receipt's cart from this list BEFORE the charge -- FE owns
   *  "what items, how many", BE owns "what total". Eliminates the
   *  mid-payment add/remove/swap drift class. Shape matches
   *  cartToRequest(cart).items. */
  items?: import('./cart').CartPreviewRequest['items']
  gift_card_number?: string
  last4?: string
  notes?: string
  reward_id?: number
  offer_id?: number
  discount_type?: string
  /** Refund tenders only: the SOURCE receipt payment JT this refund
   *  settles (from source_payments[].jt_id). Powers the BE's exact
   *  per-payment caps + gift-card transId refunds; the BE auto-binds
   *  when absent, so omitting it is always safe. */
  source_jt_id?: number
  /** Refund tenders only: credit the SOURCE receipt's online (Moneris
   *  Checkout) capture at the gateway. The BE moves the money first,
   *  then books this JT with the refund transaction id. Mutually
   *  exclusive with source_jt_id (there is no JT behind an online
   *  capture). */
  online_refund?: boolean
}

interface PaymentResponse {
  receipt: Receipt
  status?: 'already_paid' | 'duplicate_transaction'
  change_due?: number
  amount_to_pay?: number
}

// POST v2('/receipts/{id}/payments') — wrapped in {success, data: {receipt}}.
// Sends a fresh idempotency_key per logical attempt (re-used on retries of
// the exact same payload to avoid double-charging).
export function useProcessPayment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      receiptId,
      payment,
    }: {
      receiptId: number
      payment: PaymentRequest
    }): Promise<PaymentResponse> => {
      // Strip undefined fields so the wire payload doesn't carry
      // amount=undefined (axios would JSON-serialize it to null, which
      // the BE then has to decide whether to treat as "send the full
      // balance"). Explicit omission keeps the contract clean: present =
      // honor; absent = BE defaults from items[] total.
      const body: Record<string, unknown> = {
        payment_type: payment.payment_type,
        idempotency_key: idempotencyKey(),
      }
      if (payment.amount !== undefined) body.amount = payment.amount
      if (payment.source_jt_id !== undefined) body.source_jt_id = payment.source_jt_id
      if (payment.online_refund) body.online_refund = true
      if (payment.items !== undefined) body.items = payment.items
      if (payment.gift_card_number !== undefined) {
        body.gift_card_number = payment.gift_card_number
      }
      if (payment.last4 !== undefined) body.last4 = payment.last4
      if (payment.notes !== undefined) body.notes = payment.notes
      if (payment.reward_id !== undefined) body.reward_id = payment.reward_id
      if (payment.offer_id !== undefined) body.offer_id = payment.offer_id
      if (payment.discount_type !== undefined) {
        body.discount_type = payment.discount_type
      }
      const envelope = await v2api.post<V2Envelope<{ receipt: Receipt }>>(
        v2(`/receipts/${receiptId}/payments`),
        body,
      )
      return {
        receipt: envelope.data.receipt,
        status: envelope.status as PaymentResponse['status'],
      }
    },
    onSuccess: (data) => {
      queryClient.setQueryData(receiptKeys.detail(data.receipt.id), data.receipt)
    },
  })
}

// POST v2('/receipts/{id}/discount')
// Returns 403 with code:"cashier_unauthorized" if the clerk profile lacks
// can_discounts. Callers should run the manager-override PIN flow before
// retrying when that happens.
export function useApplyDiscount() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      receiptId,
      percent,
      amount,
      reasonId,
      reasonOther,
      coupon,
    }: {
      receiptId: number
      percent?: number
      amount?: number
      reasonId?: number | null
      reasonOther?: string
      coupon?: string
    }): Promise<Receipt> => {
      const payload: Record<string, unknown> = {}
      if (percent) payload.percent = percent
      if (amount) payload.amount = amount
      if (reasonId) payload.reason_id = reasonId
      if (reasonOther) payload.reason_other = reasonOther
      if (coupon) payload.coupon = coupon

      return v2api.post<Receipt>(v2(`/receipts/${receiptId}/discount`), payload)
    },
    onSuccess: (receipt) => {
      queryClient.setQueryData(receiptKeys.detail(receipt.id), receipt)
    },
  })
}

// GET v2('/objects/discount_reasons') — returns list of { id, name, percent } objects
export interface DiscountReason {
  id: number
  name: string
  percent?: number | null
}

export function useDiscountReasons() {
  return useQuery({
    queryKey: ['discount_reasons'],
    queryFn: async (): Promise<DiscountReason[]> => {
      const data = await v2api.get<DiscountReason[] | { items: DiscountReason[] }>(
        v2Unscoped('/objects/discount_reasons'),
      )
      return Array.isArray(data) ? data : (data as { items: DiscountReason[] }).items ?? []
    },
    staleTime: 1000 * 60 * 30,
  })
}

// POST v2('/receipts/{id}/pay_out') — wrapped envelope, requires idempotency_key.
export function usePayOut() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      receiptId,
      amount,
      notes,
    }: {
      receiptId: number
      amount: number
      notes?: string
    }): Promise<Receipt> => {
      const envelope = await v2api.post<V2Envelope<{ receipt: Receipt }>>(
        v2(`/receipts/${receiptId}/pay_out`),
        { amount, notes, idempotency_key: idempotencyKey() },
      )
      return envelope.data.receipt
    },
    onSuccess: (receipt) => {
      queryClient.setQueryData(receiptKeys.detail(receipt.id), receipt)
    },
  })
}

// POST v2('/receipts/{id}/items') with gift_card_number + gift_card_balance fields.
export function useSellGiftCard() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      receiptId,
      cardNumber,
      balance,
    }: {
      receiptId: number
      cardNumber: string
      balance: number
    }): Promise<Receipt> => {
      return v2api.post<Receipt>(
        v2(`/receipts/${receiptId}/items`),
        {
          gift_card_number: cardNumber,
          gift_card_balance: balance,
          qty: 1,
          price: balance,
        },
      )
    },
    onSuccess: (receipt) => {
      queryClient.setQueryData(receiptKeys.detail(receipt.id), receipt)
    },
  })
}

// GET v2('/receipts/search?on_hold=true')
export function useHeldReceipts() {
  const { selectedStore } = useSessionStore()

  return useQuery({
    queryKey: [...receiptKeys.all, 'held', selectedStore?.id],
    queryFn: async (): Promise<Receipt[]> => {
      const data = await v2api.get<Receipt[] | { receipts: Receipt[] }>(
        v2('/receipts/search'),
        { on_hold: true },
      )
      return Array.isArray(data) ? data : (data as { receipts: Receipt[] }).receipts ?? []
    },
    enabled: !!selectedStore?.id,
    staleTime: 0,
  })
}

// ============================================================================
// Orders API (Pending/Held Receipts)
// ============================================================================

export interface OrdersFilters {
  status?: number | string  // Can be single status or comma-separated
  limit?: number
  offset?: number
  customer_id?: number
}

/**
 * Get orders (pending and held receipts)
 * Orders are receipts with status 0 (New/Pending) or 3 (Hold)
 */
/**
 * Get orders (pending and held receipts)
 *
 * Note: Only returns held orders due to API performance constraints.
 * The search API with any=true is too slow (30+ seconds).
 */
export function useOrders() {
  return useHeldOrders()
}

/**
 * Get pending orders only (status = 0)
 *
 * Note: Not implemented due to API performance constraints.
 * The search endpoint doesn't have a fast way to query pending orders.
 */
export function usePendingOrders() {
  const { selectedStore } = useSessionStore()

  return useQuery({
    queryKey: [...receiptKeys.all, 'orders', 'pending', selectedStore?.id],
    queryFn: async (): Promise<any[]> => [],
    enabled: false, // Disabled - no fast API endpoint available
  })
}

/**
 * Get held orders only (status = 3)
 * Fast query using on_hold=true filter
 */
export function useHeldOrders() {
  const { selectedStore } = useSessionStore()

  return useQuery({
    queryKey: [...receiptKeys.all, 'orders', 'held', selectedStore?.id],
    queryFn: async (): Promise<any[]> => {
      const data = await v2api.get<any[]>(
        v2('/receipts/search'),
        { on_hold: true, limit: 50 },
      )
      const list = Array.isArray(data) ? data : (data as { receipts: any[] }).receipts ?? []
      return dedupeById(list)
    },
    enabled: !!selectedStore?.id,
    staleTime: 0,
  })
}

/**
 * Get ecom (formerly "kiosk") orders — receipts originated by the
 * online/self-serve channel. Mirrors the legacy waio-portal
 * `SearchReceiptKiosk` flow which hit `frontend/receipts/search` with
 * `kiosk: 'true'`. The v2 search endpoint accepts the same parameter.
 *
 * `open: true` has the BE drop Completed orders before the limit, so the
 * list is the newest 200 orders still waiting, however old. Without it the BE returned
 * the newest 50 online orders of every status and the callers dropped the
 * Completed ones, so an order still waiting for pickup fell off the Pickup
 * tab once 50 newer orders came in (about four days at a busy store). The
 * callers still drop Completed themselves, for a BE that predates `open`.
 */
export function useEcomOrders() {
  const { selectedStore } = useSessionStore()

  return useQuery({
    queryKey: [...receiptKeys.all, 'orders', 'ecom', selectedStore?.id],
    queryFn: async (): Promise<any[]> => {
      const data = await v2api.get<any[]>(
        v2('/receipts/search'),
        { kiosk: true, open: true, limit: 200 },
      )
      const list = Array.isArray(data) ? data : (data as { receipts: any[] }).receipts ?? []
      return dedupeById(list)
    },
    enabled: !!selectedStore?.id,
    staleTime: 1000 * 15,
  })
}

// GET /receipts/search?delivery=true — the Deliveries tab.
// Returns every ecom delivery order that's still outstanding (BE excludes
// the terminal delivery_status values: delivered / pickup_at_store). Fetched
// on page load alongside recent/ecom so the tab + its count are warm.
// `enabled` lets callers gate the fetch on the store's delivery setting
// (kiosk.delivery.allow_delivery via usePOSConfig). When delivery is off we
// skip the request entirely, so the Deliveries tab and its alert never warm.
export function useDeliveryOrders(enabled = true) {
  const { selectedStore } = useSessionStore()

  return useQuery({
    queryKey: [...receiptKeys.all, 'orders', 'delivery', selectedStore?.id],
    queryFn: async (): Promise<any[]> => {
      const data = await v2api.get<any[]>(
        v2('/receipts/search'),
        { delivery: true, limit: 50 },
      )
      const list = Array.isArray(data) ? data : (data as { receipts: any[] }).receipts ?? []
      return dedupeById(list)
    },
    enabled: enabled && !!selectedStore?.id,
    staleTime: 1000 * 15,
  })
}

export function useRecentPaidOrders() {
  const { selectedStore } = useSessionStore()

  return useQuery({
    queryKey: [...receiptKeys.all, 'orders', 'paid', selectedStore?.id],
    queryFn: async (): Promise<any[]> => {
      try {
        const dateStart = new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString()
        const data = await v2api.get<any[]>(
          v2('/receipts/search'),
          {
            date_start: dateStart,
            // `any: true` forces the legacy endpoint to return receipts
            // across all statuses. Without it the backend only returns the
            // active working set (held/pending), which the completed-order
            // filter below would then strip to zero.
            any: true,
            limit: 50,
          },
        )
        const list = Array.isArray(data) ? data : (data as { receipts: any[] }).receipts ?? []
        return dedupeById(list.filter(isCompletedOrder))
      } catch (error) {
        console.error('Failed to fetch paid orders:', error)
        return []
      }
    },
    enabled: !!selectedStore?.id,
    staleTime: 1000 * 15,
    retry: 1,
  })
}

/**
 * Search all receipts with a query string
 * Fast when a search query is provided (uses indexed search)
 */
// Default search window when `allTime` is false. Receipt search is a BE
// seq-scan with no trigram index; capping to recent activity keeps the
// common cashier flow ("look up Jeremy's last receipt") under a second
// instead of scanning the full receipts table.
const SEARCH_DEFAULT_WINDOW_DAYS = 90

export function useSearchOrders(searchQuery: string, options: { allTime?: boolean } = {}) {
  const { selectedStore } = useSessionStore()
  const { allTime = false } = options

  return useQuery({
    queryKey: [...receiptKeys.all, 'orders', 'search', selectedStore?.id, searchQuery, allTime],
    queryFn: async ({ signal }): Promise<any[]> => {
      if (!searchQuery || searchQuery.length < 2) {
        return []
      }

      // Pass the React Query AbortSignal so a superseded keystroke
      // cancels the old in-flight request — otherwise the BE keeps
      // running the slow seq-scan on every intermediate query.
      const params: Record<string, unknown> = { q: searchQuery, any: true, limit: 50 }
      if (!allTime) {
        const windowMs = SEARCH_DEFAULT_WINDOW_DAYS * 24 * 60 * 60 * 1000
        params.date_start = new Date(Date.now() - windowMs).toISOString()
      }

      const data = await v2api.get<any[]>(
        v2('/receipts/search'),
        params,
        { signal },
      )
      const list = Array.isArray(data) ? data : (data as { receipts: any[] }).receipts ?? []
      return dedupeById(list.filter(isCompletedOrder))
    },
    enabled: !!selectedStore?.id && !!searchQuery && searchQuery.length >= 2,
    staleTime: 0,
  })
}

/**
 * Cancel an order — POST v2('/receipts/{id}/cancel-order').
 * v2 rejects cancel-order on paid receipts (HTTP 409,
 * code: cannot_cancel_paid). Callers should disable the cancel button on
 * paid receipts and route to the refund flow instead.
 */
export function useCancelOrder() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ receiptId }: { receiptId: number }): Promise<Receipt> => {
      return v2api.post<Receipt>(v2(`/receipts/${receiptId}/cancel-order`), {})
    },
    onSuccess: () => {
      // Held POS receipts can be cancelled through this hook too (not
      // just ecom orders -- see OrderDetailModal.canCancelOrder).
      // Cancelling a held receipt clears on_hold server-side, so it
      // drops out of the held list -- but the cached query needs to
      // be invalidated for the UI to reflect that.
      queryClient.invalidateQueries({ queryKey: [...receiptKeys.all, 'orders'] })
      queryClient.invalidateQueries({ queryKey: [...receiptKeys.all, 'held'] })
    },
  })
}

// Receipt status constants — values mirror waio-portal's
// `PosReceiptStatus` (project/frontend/models/pos.py:161-167).
//
// IMPORTANT: an earlier mapping in this file was off-by-one (PAID was 2,
// PROCESSING was 1, VOID was 4, REFUND existed). That caused paid
// receipts to be filtered out of `useSearchOrders` — they showed up in
// the legacy POS but waio-pos's Orders tab returned "No results found"
// for receipts the cashier had just paid. Matched against backend
// reality below; do not "tidy up" without checking the source enum.
export const RECEIPT_STATUS = {
  DELETED: -1,
  NEW: 0,
  PENDING: 0,    // alias for NEW (cart in progress)
  PAID: 1,
  PAYING: 2,
  HOLD: 4,
  CANCELED: 8,
  // Ecom: paid via the online checkout (Moneris Checkout iframe); funds
  // captured, customer still has to pick up. Completed via
  // /complete-pickup (useCompletePickup), NEVER via the /status setter.
  PAID_ONLINE: 32,
} as const

// Statuses that the backend treats as terminal / locked. Mirrors
// PosReceiptStatus.LOCKED (Paid, Paying, Deleted, Canceled).
export const LOCKED_RECEIPT_STATUSES = new Set<number>([
  RECEIPT_STATUS.PAID,
  RECEIPT_STATUS.PAYING,
  RECEIPT_STATUS.DELETED,
  RECEIPT_STATUS.CANCELED,
])

// The Orders tab surfaces completed transactions only — paid sales and
// their refunds. We filter by *exclusion* rather than requiring
// `status === PAID` because the legacy backend sometimes leaves the
// status field at 0 (or unset) on lightweight search payloads.
//
// Excluded statuses (per waio-portal PosReceiptStatus):
//   New(0)      — cart still in progress
//   Paying(2)   — payment in flight, not yet confirmed
//   Hold(4)     — held order, surfaced via the Hold tab
//   Deleted(-1) — soft-deleted
//   Canceled(8) — voided
// Dedupe order rows by `id` — the BE's /receipts/search response can
// return the same receipt twice (most often a refund whose underlying
// query joins to multiple matching rows). Without this, the duplicate
// renders as two identical OrderCards and React also warns about the
// repeated `key`. First occurrence wins.
function dedupeById<T extends { id: number | string }>(list: T[]): T[] {
  const seen = new Set<T['id']>()
  const out: T[] = []
  for (const item of list) {
    if (item == null || item.id == null) continue
    if (seen.has(item.id)) continue
    seen.add(item.id)
    out.push(item)
  }
  return out
}

function isCompletedOrder(r: {
  status?: number | string | null
  on_hold?: boolean
  total?: number | string | null
  totals?: { total?: number | string | null } | null
  is_return?: boolean
  src_id?: number | null
  source_number?: string | number | null
  return_number?: string | number | null
  return_total?: number | string | null
  return_receipt_id?: number | null
}): boolean {
  if (r?.on_hold === true) return false
  const status = Number(r?.status)
  if (status === RECEIPT_STATUS.NEW) return false
  if (status === RECEIPT_STATUS.PAYING) return false
  if (status === RECEIPT_STATUS.HOLD) return false
  if (status === RECEIPT_STATUS.DELETED) return false
  if (status === RECEIPT_STATUS.CANCELED) return false
  // PaidOnline = paid but NOT picked up: it belongs in the Pickup/ecom
  // tab (the BE flips kiosk_customer_status to Paid at capture), not in
  // completed orders. It graduates here once complete-pickup runs
  // (status -> PAID).
  if (status === RECEIPT_STATUS.PAID_ONLINE) return false
  // Orphan negative totals are refunds not linked back to a source receipt
  // — skip those; keep refunds that have a return link.
  //
  // Link fields differ by which side of the relationship the receipt is:
  //   - REFUND side (the receipt created via copy_with_refund):
  //     `is_return: true`, `src_id`, `source_number` point back to the
  //     original sale.
  //   - SOURCE side (the original sale receipt that's been refunded):
  //     `return_number`, `return_total`, `return_receipt_id` point
  //     forward to the refund. Mostly relevant for v1 data; v2 uses
  //     `is_return` on the refund side.
  // We treat any of these as "this receipt has a return link" so refunds
  // surface in the Orders list regardless of which fields the backend
  // populated.
  const total = parseFloat(String(r?.totals?.total ?? r?.total ?? 0))
  const hasReturnLink = Boolean(
    r?.is_return === true
    || r?.src_id
    || r?.source_number
    || r?.return_number
    || r?.return_total
    || r?.return_receipt_id,
  )
  if (total < 0 && !hasReturnLink) return false
  return true
}

// ============================================================================
// Refund/Return API
// ============================================================================

export interface ReturnReason {
  id: number
  name: string
}

/**
 * Get list of return reasons
 * GET v2('/objects/return_reasons')
 */
export function useReturnReasons() {
  return useQuery({
    queryKey: ['return_reasons'],
    queryFn: async (): Promise<ReturnReason[]> => {
      const data = await v2api.get<ReturnReason[] | { items: ReturnReason[] }>(
        v2Unscoped('/objects/return_reasons'),
      )
      return Array.isArray(data) ? data : (data as { items: ReturnReason[] }).items ?? []
    },
    staleTime: 1000 * 60 * 30, // 30 minutes
  })
}

/**
 * Search receipts for refund (excludes returns by default)
 * GET v2('/receipts/search?flags=no-returns&q={query}')
 */
export function useSearchReceiptsForRefund(searchQuery: string, enabled = true) {
  const { selectedStore } = useSessionStore()

  return useQuery({
    queryKey: [...receiptKeys.all, 'refund-search', selectedStore?.id, searchQuery],
    queryFn: async (): Promise<any[]> => {
      const data = await v2api.get<any[]>(
        v2('/receipts/search'),
        {
          q: searchQuery || '',
          flags: 'no-returns',
        },
      )
      const receipts = Array.isArray(data) ? data : (data as { receipts: any[] }).receipts ?? []

      return receipts.filter((r: any) => {
        // Exclude refund/return receipts (negative totals or is_return flag)
        if (r.is_return === true) return false
        // Exclude voided / soft-deleted receipts. Backend statuses:
        //   Canceled(8), Deleted(-1) — see RECEIPT_STATUS.
        if (r.status === RECEIPT_STATUS.CANCELED) return false
        if (r.status === RECEIPT_STATUS.DELETED) return false
        // Keep source receipts visible (including already-refunded ones),
        // but hide likely refund receipts that appear as standalone negative totals.
        const total = parseFloat(String(r.totals?.total ?? r.total ?? 0))
        const hasReturnLink = Boolean(r.return_number || r.return_total || r.return_receipt_id)
        if (total < 0 && !hasReturnLink) return false
        return true
      })
    },
    enabled: !!selectedStore?.id && enabled,
    staleTime: 0, // Always refetch to reflect newly refunded receipts
    retry: 1,
    refetchOnWindowFocus: false,
  })
}

/**
 * Create a refund receipt from a source receipt.
 * POST v2('/receipts/copy-with-refund') — wrapped in
 * {success, data: {receipt, journal_transaction_id, source_payments}}.
 */
export function useCreateRefund() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      sourceReceiptId,
      isReturn = true,
    }: {
      sourceReceiptId: number
      isReturn?: boolean
    }): Promise<Receipt & { source_payments?: any[]; journal_transaction_id?: number }> => {
      const envelope = await v2api.post<V2Envelope<{
        receipt: Receipt
        journal_transaction_id: number
        source_payments: any[]
      }>>(
        v2('/receipts/copy-with-refund'),
        {
          src_id: sourceReceiptId,
          is_return: isReturn,
        },
      )
      return {
        ...envelope.data.receipt,
        journal_transaction_id: envelope.data.journal_transaction_id,
        source_payments: envelope.data.source_payments,
      }
    },
    onSuccess: (receipt) => {
      if (receipt.id) {
        queryClient.setQueryData(receiptKeys.detail(receipt.id), receipt)
        queryClient.invalidateQueries({ queryKey: [...receiptKeys.all, 'orders'] })
        queryClient.invalidateQueries({ queryKey: [...receiptKeys.all, 'refund-search'] })
      }
    },
  })
}

/**
 * Process refund payment with return reason.
 * POST v2('/receipts/{id}/payments') — wrapped envelope, requires
 * idempotency_key (fresh per logical attempt, reused on retry).
 */
export function useProcessRefundPayment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      receiptId,
      payment,
      returnReasonId,
      returnReasonText,
    }: {
      receiptId: number
      payment: PaymentRequest
      returnReasonId?: number
      returnReasonText?: string
    }): Promise<PaymentResponse> => {
      const payload: Record<string, unknown> = {
        payment_type: payment.payment_type,
        idempotency_key: idempotencyKey(),
      }
      if (payment.amount !== undefined) payload.amount = payment.amount
      if (payment.source_jt_id !== undefined) payload.source_jt_id = payment.source_jt_id
      if (payment.online_refund) payload.online_refund = true
      if (payment.items !== undefined) payload.items = payment.items
      if (payment.gift_card_number !== undefined) {
        payload.gift_card_number = payment.gift_card_number
      }
      if (payment.last4 !== undefined) payload.last4 = payment.last4
      if (payment.notes !== undefined) payload.notes = payment.notes
      if (payment.reward_id !== undefined) payload.reward_id = payment.reward_id
      if (payment.offer_id !== undefined) payload.offer_id = payment.offer_id
      if (payment.discount_type !== undefined) {
        payload.discount_type = payment.discount_type
      }
      if (returnReasonId) payload.return_reason_id = returnReasonId
      if (returnReasonText) payload.return_reason_text = returnReasonText

      const envelope = await v2api.post<V2Envelope<{ receipt: Receipt }>>(
        v2(`/receipts/${receiptId}/payments`),
        payload,
      )
      return {
        receipt: envelope.data.receipt,
        status: envelope.status as PaymentResponse['status'],
      }
    },
    onSuccess: (data) => {
      queryClient.setQueryData(receiptKeys.detail(data.receipt.id), data.receipt)
      queryClient.invalidateQueries({ queryKey: [...receiptKeys.all, 'orders'] })
      queryClient.invalidateQueries({ queryKey: [...receiptKeys.all, 'refund-search'] })
    },
  })
}
